import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { HERO_IMAGE } from "../lib/constants";
import { GoogleLogo, ArrowRight } from "@phosphor-icons/react";

export default function Login() {
  const { loginJwt, registerJwt } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/profile";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await loginJwt(email, password);
        toast.success("Bentornato!");
      } else {
        await registerJwt(email, password, name);
        toast.success("Account creato!");
      }
      navigate("/profile");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore autenticazione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] grid lg:grid-cols-2">
      {/* Left: Image */}
      <div className="hidden lg:block relative">
        <img src={HERO_IMAGE} alt="Login" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/30 to-black/80" />
        <div className="grain-overlay" />
        <div className="absolute bottom-12 left-12 right-12">
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-primary mb-4">The Community</div>
          <h2 className="font-display text-4xl font-black uppercase tracking-tighter leading-none">Unisciti a migliaia di appassionati</h2>
        </div>
      </div>

      {/* Right: Form */}
      <div className="flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md">
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-primary mb-3">{mode === "login" ? "Bentornato" : "Nuovo qui"}</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter mb-8" data-testid="auth-title">
            {mode === "login" ? "Accedi" : "Registrati"}
          </h1>

          <Button
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
            variant="outline"
            className="w-full h-12 border-border rounded-sm uppercase tracking-wider text-xs font-bold hover:border-primary hover:text-primary mb-6"
          >
            <GoogleLogo size={16} weight="bold" className="mr-2" /> Continua con Google
          </Button>

          <div className="flex items-center gap-4 mb-6">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-[10px] uppercase tracking-widest text-neutral-500">oppure</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-neutral-400 mb-2 block">Nome</label>
                <Input data-testid="auth-name" value={name} onChange={e => setName(e.target.value)} required className="bg-card border-border rounded-sm h-12" />
              </div>
            )}
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-neutral-400 mb-2 block">Email</label>
              <Input data-testid="auth-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required className="bg-card border-border rounded-sm h-12" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-neutral-400 mb-2 block">Password</label>
              <Input data-testid="auth-password" type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={6} className="bg-card border-border rounded-sm h-12" />
            </div>
            <Button type="submit" disabled={loading} data-testid="auth-submit" className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-black uppercase tracking-wider rounded-sm">
              {loading ? "..." : (mode === "login" ? "Accedi" : "Crea account")} <ArrowRight size={14} weight="bold" className="ml-2" />
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-neutral-400">
            {mode === "login" ? "Non hai un account?" : "Hai già un account?"}{" "}
            <button data-testid="auth-toggle" onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-primary font-bold uppercase tracking-wider text-xs">
              {mode === "login" ? "Registrati" : "Accedi"}
            </button>
          </div>

          <div className="mt-8 text-center">
            <Link to="/" className="text-xs text-neutral-500 uppercase tracking-widest hover:text-primary">← Torna alla home</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
